// Centralized constants for the ITAM-2583 consult-order flow.
//
// Every timing/cadence/cap and the confirmed 15-minute grace window live here so
// they stay trivial to tune in one place (plan: "centralized as a single
// constant ... so it stays trivial to change").

/**
 * Bounded grace window (Q15, CONFIRMED = 15 min). After the consult ends while a
 * form/draft has been started, the draft stays submittable for this long; a NEW
 * draft cannot be started once the consult is inactive.
 */
export const CONSULT_ORDER_GRACE_MS = 15 * 60 * 1000;

// --- Trigger poll (fires after the pharmacist clicks "Join") ---------------
/** Cadence of the post-arm prefill poll. */
export const TRIGGER_POLL_INTERVAL_MS = 3500;
/** Hard cap for the trigger poll; exhaustion returns the machine to IDLE. */
export const TRIGGER_POLL_MAX_MS = 2 * 60 * 1000;

// --- Mount probe (refresh / multi-tab recovery) ----------------------------
/** Cadence of the short bounded probe run on eligible mount. */
export const MOUNT_PROBE_INTERVAL_MS = 5000;
/** Bounded window for the mount probe; only ever reveals the chip, never auto-opens. */
export const MOUNT_PROBE_MAX_MS = 45 * 1000;

// --- Keep-alive poll (runs while the form is open) -------------------------
/** Low-frequency poll used to detect the consult ending (200 -> 404). */
export const KEEPALIVE_POLL_INTERVAL_MS = 12000;
/**
 * Number of CONSECUTIVE 404s required before treating the consult as ended
 * (debounces a single transient 404 during an active consult).
 */
export const KEEPALIVE_404_THRESHOLD = 2;

// --- Item stock overview ----------------------------------------------------
/** numOfReturns for GET /item_in_marketplace/stock_overview (rows are heavy). */
export const STOCK_OVERVIEW_PAGE_SIZE = 10;

// --- Delivery pre-checks ----------------------------------------------------
/** standardDeliveryDays parameter for GET /check_delivery_time_slots. */
export const STANDARD_DELIVERY_DAYS = 2;

// --- Google Places (phase-2) -----------------------------------------------
/** Prediction/geocode language for the Places widget (plan: sr-Latn). */
export const PLACES_LANGUAGE = "sr-Latn";
/** ISO region for Places (Serbia). */
export const PLACES_REGION = "RS";
/** Restrict predictions to Serbia. */
export const PLACES_INCLUDED_REGION_CODES = ["rs"];
/** Soft locationBias radius (metres) around the branch coordinates. */
export const PLACES_LOCATION_BIAS_RADIUS_M = 25000;
/** Minimal Place Details field mask (Essentials SKU only — never add Pro fields). */
export const PLACES_DETAIL_FIELDS = [
  "location",
  "formattedAddress",
  "addressComponents",
] as const;

// --- Delivery fee payment ---------------------------------------------------
/**
 * Paying the delivery fee with loyalty points is hidden: the caller is only
 * offered RSD. Mirrors the mobile kill switches shipped for ITAM-2756 (iOS
 * `DetailsDeliveryViewModel.isLoyaltyPaymentEnabled`) and ITAM-2757 (Android
 * `AppConfig.DELIVERY_FEE_LOYALTY_PAYMENT_ENABLED`), both `false`.
 *
 * Flip to `true` to bring the choice back in one line. The backend contract is
 * untouched: `CncOrderDeliveryPaymentType` still carries both members, and the
 * payload still sends an explicit `deliveryPaymentType` (now always "CASH").
 * That matters, because apoteka-back only materialises the SERVICE fee as an order
 * line and rolls it into the totals on the CASH branch; LOYALTY_POINTS instead
 * debits the caller's points.
 */
export const CONSULT_LOYALTY_PAYMENT_ENABLED: boolean = false;
